<template>
  <div v-if="open" class="modal fade show d-block currency-modal" tabindex="-1" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("currency_page.edit_chip") : $t("currency_page.new_chip") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1">
              {{ isEdit ? $t("currency_page.update_title") : $t("currency_page.create_title") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("currency_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("currency_page.code") }}</label>
                <span class="field-caption">{{ $t("currency_page.code_caption") }}</span>
              </div>
              <input
                :value="form.code"
                type="text"
                maxlength="10"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.code }"
                :placeholder="$t('currency_page.code_placeholder')"
                @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value.toUpperCase())"
              />
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("currency_page.name") }}</label>
                <span class="field-caption">{{ $t("currency_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('currency_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-4">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("currency_page.symbol") }}</label>
                <span class="field-caption">{{ $t("currency_page.symbol_caption") }}</span>
              </div>
              <input
                :value="form.symbol"
                type="text"
                maxlength="5"
                class="form-control modal-input"
                :placeholder="$t('currency_page.symbol_placeholder')"
                @input="$emit('update-field', 'symbol', ($event.target as HTMLInputElement).value)"
              />
            </div>

            <div class="col-md-8">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("currency_page.rate") }}</label>
                <span class="field-caption">{{ $t("currency_page.rate_caption") }}</span>
              </div>
              <input
                :value="form.rate"
                type="number"
                step="0.000001"
                min="0"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.rate }"
                :placeholder="$t('currency_page.rate_placeholder')"
                @input="$emit('update-field', 'rate', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.rate" class="invalid-feedback d-block">{{ errors.rate[0] }}</div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button type="button" class="btn style-one px-4 h-55" :disabled="loading" @click="$emit('save')">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("currency_page.saving") : isEdit ? $t("currency_page.update_action") : $t("currency_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Currency } from "@/services/currency";

export default defineComponent({
  name: "CreateCurrencyModal",
  emits: ["close", "save", "update-field"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<Currency>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
});
</script>

<style scoped>
.currency-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.modal-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}
</style>
